import { useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { Recipe } from '../types/recipe';
import { getAuthHeaders, ApiError, AUTH_REQUIRED_CODE, isAuthRequiredError, isSessionTokenError } from '../services/apiClient';
import { getPersistableRecipeId, classifyRecipeId } from '../utils/recipeIds';
import { trackEvent } from '../services/analyticsService';
import { getLastFailedScrapeAt, recordFailedScrape } from '../utils/scrapeBackoff';
import { getApiBaseUrl } from '../config/apiBase';
import { parseRecipeDuration as parseIsoDuration } from '../utils/parseRecipeDuration';

interface EnrichmentResult {
  readyInMinutes?: number;
  preparationMinutes?: number;
  cookingMinutes?: number;
  servings?: number;
  image?: string;
  lastScrapedAt?: string;
  sourceRating?: number;
  sourceRatingCount?: number;
  sourceRatingScale?: number;
  enrichmentAttemptCount?: number;
}

// Module-level set shared across all hook instances so the same recipe is never
// enriched twice in a session — even if the component unmounts/remounts or both
// the recipes screen and the weekly screen are mounted simultaneously.
const _sessionProcessedIds = new Set<string>();

/** Exposed for test isolation only — do not call in production code. */
export function _clearEnrichmentSessionCache(): void {
  _sessionProcessedIds.clear();
}

const MAX_ENRICHMENT_ATTEMPTS = 3;

/**
 * The scraper answers 400 to anything that is not an http(s) address (#778).
 * Its other refusals (private hosts, social sites) are left to it: a list kept
 * here would drift from the server's, and the attempt cap bounds them anyway.
 */
function isWebAddress(url: string | undefined): boolean {
  if (!url) return false;
  try {
    const { protocol } = new URL(url);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Decide whether a recipe should be background-enriched (scraped for cook-time
 * and source-rating metadata).
 *
 * A recipe gets up to `MAX_ENRICHMENT_ATTEMPTS` tries across sessions, tracked
 * via `enrichmentAttemptCount` (incremented on every attempt, success or
 * failure). Earlier logic gated on `!lastScrapedAt`, so once ANY scrape ever
 * set that timestamp — even one that came back with no rating — the recipe
 * was never re-enrolled, permanently freezing its metadata gaps (#460). Before
 * that, the logic gated on `(!lastScrapedAt || !sourceRating)` with no cap at
 * all, re-scraping a rating-less recipe every single session and hammering
 * paywalled/blocked sources (the storm in #381/#391/#392). A bounded attempt
 * count avoids both failure modes. Content (ingredients/instructions) is never
 * fetched here — that is the recipe detail screen's job; enrichment is
 * best-effort metadata only.
 */
export function recipeNeedsEnrichment(
  recipe: Pick<Recipe, 'id' | 'readyInMinutes' | 'sourceUrl' | 'sourceRating' | 'enrichmentAttemptCount'>,
  processedIds: Set<string>
): boolean {
  const missingMetadata =
    !recipe.readyInMinutes || recipe.readyInMinutes === 0 || !recipe.sourceRating;
  return (
    missingMetadata &&
    isWebAddress(recipe.sourceUrl) &&
    (recipe.enrichmentAttemptCount ?? 0) < MAX_ENRICHMENT_ATTEMPTS &&
    !processedIds.has(String(recipe.id))
  );
}

/**
 * Hook that automatically enriches recipes with missing cook time in the background.
 * Processes recipes sequentially with rate limiting to avoid API overload.
 *
 * @param recipes - Array of recipes to check for enrichment
 * @param onRecipeEnriched - Callback when a recipe is enriched with new data
 */
export function useRecipeEnrichment(
  recipes: Recipe[],
  onRecipeEnriched: (recipeId: string, data: EnrichmentResult) => void
) {
  const user = useSelector((state: RootState) => state.auth?.user);
  const isProcessingRef = useRef(false);

  // Memoize the enrichment function
  const enrichRecipe = useCallback(async (recipe: Recipe): Promise<EnrichmentResult | null> => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      const scrapeUrl = `${apiBaseUrl}/api/recipes/scrape`;

      const scrapeHeaders = await getAuthHeaders();
      const response = await fetch(scrapeUrl, {
        method: 'POST',
        headers: scrapeHeaders,
        body: JSON.stringify({ url: recipe.sourceUrl }),
      });

      if (response.status === 401) {
        // Not the site's failure: thrown so the queue stops and records nothing.
        throw new ApiError('Missing auth token', 401, AUTH_REQUIRED_CODE);
      }
      if (!response.ok) {
        console.warn(`Enrichment failed for ${recipe.title}: HTTP ${response.status}`);
        return null;
      }

      const { success, recipe: scrapedRecipe } = await response.json();
      if (!success || !scrapedRecipe) {
        console.warn(`Enrichment failed for ${recipe.title}: No data returned`);
        return null;
      }

      const result: EnrichmentResult = {
        lastScrapedAt: new Date().toISOString(),
      };

      // Parse time fields
      const totalTime = parseIsoDuration(scrapedRecipe.totalTime || '');
      const prepTime = parseIsoDuration(scrapedRecipe.prepTime || '');
      const cookTime = parseIsoDuration(scrapedRecipe.cookTime || '');

      if (totalTime > 0) {
        result.readyInMinutes = totalTime;
      } else if (cookTime > 0 || prepTime > 0) {
        result.readyInMinutes = prepTime + cookTime;
      }

      if (prepTime > 0) {
        result.preparationMinutes = prepTime;
      }

      if (cookTime > 0) {
        result.cookingMinutes = cookTime;
      }

      // Parse servings
      if (scrapedRecipe.recipeYield) {
        const servingsNum = parseInt(scrapedRecipe.recipeYield);
        if (!isNaN(servingsNum) && servingsNum > 0) {
          result.servings = servingsNum;
        }
      }

      // Image
      if (scrapedRecipe.image && !recipe.image) {
        result.image = scrapedRecipe.image;
      }

      // Source rating
      if (scrapedRecipe.sourceRating != null) {
        result.sourceRating = scrapedRecipe.sourceRating;
      }
      if (scrapedRecipe.sourceRatingCount != null) {
        result.sourceRatingCount = scrapedRecipe.sourceRatingCount;
      }
      if (scrapedRecipe.sourceRatingScale != null) {
        result.sourceRatingScale = scrapedRecipe.sourceRatingScale;
      }

      return result;
    } catch (error) {
      if (isAuthRefusal(error)) throw error;
      console.warn(`Enrichment error for ${recipe.title}:`, error);
      return null;
    }
  }, []);

  // Trigger server-side enrichment. The server re-scrapes from the recipe's
  // own sourceUrl — the client cannot influence what fields are written. The
  // local Redux update above uses the client's scrape view for immediate UI
  // feedback; the server may pick slightly different values for the
  // persisted doc. `attemptCount` is always sent so it's persisted even when
  // this particular attempt found nothing — that's what makes the cap in
  // `recipeNeedsEnrichment` actually bounded across app restarts.
  const persistEnrichment = useCallback(async (recipeId: string, attemptCount: number) => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      const enrichUrl = `${apiBaseUrl}/api/recipes/enrich`;

      const enrichHeaders = await getAuthHeaders();
      const hadToken = !!enrichHeaders.Authorization;
      const response = await fetch(enrichUrl, {
        method: 'POST',
        headers: enrichHeaders,
        body: JSON.stringify({ recipeId, enrichmentAttemptCount: attemptCount }),
      });

      if (!response.ok) {
        // 404 is expected for recipes not yet in Firestore — skip silently
        if (response.status !== 404) {
          console.warn(`Failed to persist enrichment for ${recipeId}: HTTP ${response.status}`);
          // Persist failures were invisible outside the console — the 403
          // storm in #496 ran for weeks unnoticed. Track them so a regression
          // shows up in PostHog.
          trackEvent('recipe_enrichment_persist_failed', {
            recipe_id: recipeId,
            http_status: response.status,
            // A 401 is either no token or a token the server refused (#778).
            had_token: hadToken,
          });
        }
      }
    } catch (error) {
      // Network errors are non-critical for enrichment persistence
      if (__DEV__) {
        console.warn('Failed to persist enrichment:', error);
      }
    }
  }, []);

  useEffect(() => {
    if (!user?.uid || isProcessingRef.current) return;

    // Cheap pre-check before touching storage: most renders have nothing to do.
    if (!recipes.some(r => recipeNeedsEnrichment(r, _sessionProcessedIds))) return;

    let cancelled = false;
    isProcessingRef.current = true;

    (async () => {
      // Device-local backoff (#668): a site that answered 422 yesterday will
      // answer 422 today. Shared with the recipe-detail heal (#673).
      const backedOff = new Set<string>();
      for (const r of recipes) {
        if (await getLastFailedScrapeAt(String(r.id))) backedOff.add(String(r.id));
      }
      if (cancelled) return;

      const batch = selectEnrichmentBatch(recipes, _sessionProcessedIds, backedOff);
      if (batch.length === 0) return;

      console.log(`🔄 Background enrichment: ${batch.length} recipes to process`);

      await runEnrichmentQueue({
        recipes: batch,
        hasToken: async () => {
          try {
            return !!(await getAuthHeaders()).Authorization;
          } catch {
            // A SessionTokenError: signed in, no usable token right now.
            return false;
          }
        },
        enrich: enrichRecipe,
        onEnriched: onRecipeEnriched,
        persist: persistEnrichment,
        recordFailure: recordFailedScrape,
        onUnresolvableId: (recipeId: string) =>
          trackEvent('recipe_enrichment_unresolvable_id', { recipe_id: recipeId }),
        processedIds: _sessionProcessedIds,
        isCancelled: () => cancelled,
        sleep: () => new Promise(resolve => setTimeout(resolve, ENRICHMENT_DELAY_MS)),
      });
    })().finally(() => {
      if (!cancelled) isProcessingRef.current = false;
    });

    // Cleanup: stop the loop when the screen unmounts or its recipe list is
    // replaced. Before #668 the loop kept going, and the boot's second
    // `setRecipes` started a second batch on top of the first.
    return () => {
      cancelled = true;
      isProcessingRef.current = false;
    };
  }, [user?.uid, recipes, enrichRecipe, onRecipeEnriched, persistEnrichment]);
}

const ENRICHMENT_BATCH_SIZE = 5;
const ENRICHMENT_DELAY_MS = 2000;

/**
 * The recipes to enrich this run: those still owed an attempt, minus the ones
 * handled this session and the ones whose last attempt failed recently.
 */
export function selectEnrichmentBatch<T extends Parameters<typeof recipeNeedsEnrichment>[0]>(
  recipes: T[],
  processedIds: Set<string>,
  backedOffIds: Set<string>,
): T[] {
  const batch: T[] = [];
  for (const recipe of recipes) {
    if (batch.length >= ENRICHMENT_BATCH_SIZE) break;
    if (backedOffIds.has(String(recipe.id))) continue;
    if (recipeNeedsEnrichment(recipe, processedIds)) batch.push(recipe);
  }
  return batch;
}

type EnrichableRecipe = {
  id: string | number;
  _firestoreId?: string;
  title: string;
  enrichmentAttemptCount?: number;
};

/**
 * The request was, or would have been, refused for want of a session: the
 * server's 401, or a `SessionTokenError` thrown before the request (#778).
 */
const isAuthRefusal = (error: unknown): boolean =>
  isSessionTokenError(error) || isAuthRequiredError(error);

export interface EnrichmentQueueDeps<T extends EnrichableRecipe> {
  recipes: T[];
  /**
   * Whether a request would carry an ID token. Firebase keeps a restored user
   * when it cannot refresh an expired token (a tablet waking on a slow
   * network), so a signed-in screen does not prove there is one (#778).
   */
  hasToken?: () => Promise<boolean>;
  /**
   * May throw an auth refusal (a 401, or a `SessionTokenError`); the queue then
   * stops and records nothing against the recipe. Any other failure is `null`.
   */
  enrich: (recipe: T) => Promise<EnrichmentResult | null>;
  /**
   * Called only when an attempt found something; the screen redraws on it.
   * Keyed by the document id, which is what a planned meal stores as recipeId.
   */
  onEnriched: (recipeId: string, data: EnrichmentResult) => void;
  /**
   * Called on every attempt with a resolvable id: the attempt count is what
   * bounds retries across sessions. Keyed by the document id — the server looks
   * the recipe up by it, and an imported recipe's `id` is a hash (#778).
   */
  persist: (recipeId: string, attemptCount: number) => Promise<void> | void;
  /** Keyed by `recipe.id`, like the session set and the batch filter. */
  recordFailure: (recipeId: string) => Promise<void>;
  /** A hashed id with no document id: nothing on the server to write to. */
  onUnresolvableId?: (recipeId: string) => void;
  processedIds: Set<string>;
  isCancelled: () => boolean;
  sleep: () => Promise<void>;
}

/**
 * Process one batch serially. Pure apart from its injected effects, so the
 * three #668 behaviours (no redraw on failure, backoff recorded, stop when
 * cancelled) are tested directly.
 */
export async function runEnrichmentQueue<T extends EnrichableRecipe>(
  deps: EnrichmentQueueDeps<T>,
): Promise<void> {
  for (const recipe of deps.recipes) {
    if (deps.isCancelled()) return;
    // Without a token every request is a 401, and recording it would put a
    // healthy site on a 24h backoff. Leave the rest for a later run.
    if (deps.hasToken && !(await deps.hasToken())) return;
    if (deps.isCancelled()) return;
    const id = String(recipe.id);
    const docId = getPersistableRecipeId(recipe);
    // Mark as processed immediately to prevent re-processing
    deps.processedIds.add(id);

    console.log(`📡 Enriching: ${recipe.title}`);
    let result: EnrichmentResult | null;
    try {
      result = await deps.enrich(recipe);
    } catch (error) {
      // No session, no verdict: leave the recipe for a run that has one.
      if (!isAuthRefusal(error)) throw error;
      deps.processedIds.delete(id);
      return;
    }
    // Increment regardless of outcome — a failed/empty attempt still counts
    // toward the cap, so a source that structurally lacks a rating stops being
    // retried after MAX_ENRICHMENT_ATTEMPTS instead of being hammered forever.
    const nextAttemptCount = (recipe.enrichmentAttemptCount ?? 0) + 1;

    if (result && ((result.readyInMinutes && result.readyInMinutes > 0) || result.sourceRating != null)) {
      console.log(`✅ Enriched ${recipe.title}: ${result.readyInMinutes ? result.readyInMinutes + 'min' : ''}${result.sourceRating != null ? ' rating:' + result.sourceRating : ''}`);
      deps.onEnriched(docId, { ...result, enrichmentAttemptCount: nextAttemptCount });
    } else {
      // Nothing to show, so nothing to redraw (#668: every failure used to
      // re-filter and re-render the whole recipe list). Remember it so the
      // next boot does not ask the same site again for 24h.
      console.log(`⏭️ No cook time found for: ${recipe.title}`);
      await deps.recordFailure(id);
    }

    // Persist to Firestore in background — server re-scrapes from sourceUrl,
    // ignoring the client-side `result`. A bare hash would only ever 404.
    if (classifyRecipeId(docId) === 'hashed_numeric') {
      deps.onUnresolvableId?.(docId);
    } else {
      await deps.persist(docId, nextAttemptCount);
    }

    if (deps.isCancelled()) return;
    // Rate limiting between requests
    await deps.sleep();
  }
}
